<template>
  <!--
    WB-M1B S4 / S4.1 (doc 23 §23, §25) -- the Equation editor of the board:
    one surface, two modes. INSERT starts empty; EDIT starts on the equation
    being edited.

    S4.1: the Student composes the equation in a VISUAL math field -- typed
    keys and the palette below it build formatted mathematics in the field
    itself (a fraction with two slots, a root, an exponent ...). There is no
    LaTeX box and no separate preview: the field is what the equation looks
    like. Underneath, the field's content is a LaTeX string
    (`boardEquationField.js`), and that string is the DRAFT.

    The dialog owns the draft and nothing else. It never touches the board.
    What the board accepts is still decided by the board's own validator and
    renderer (`boardEquation.js`, S1): the primary action is available only
    for a draft they draw. The Workspace is told `submit` with the draft.

    Opening an equation does not change it: until the Student edits, the
    draft is the stored source byte for byte, however the field would write
    it. Closing (`cancel`: the button, Escape, the backdrop) throws the draft
    away.

    The field is loaded when the dialog opens (its own chunk); the math
    engine by the first draft that can be drawn.

    The dialog shape is the Student shell's (StudentComingSoonDialog): a
    centred dialog, a sheet at the bottom on a phone, Escape and the Tab
    containment handled once on `document`, focus handed back on close. On a
    phone the sheet follows the visual viewport, so it stays above the
    on-screen keyboard.
  -->
  <div
    class="ai-tutor-equation-dialog__backdrop"
    data-testid="ai-tutor-equation-dialog"
    :data-mode="mode"
    :style="viewportStyle"
    @mousedown="pressedOutside = $event.target === $event.currentTarget"
    @click.self="onBackdropClick"
  >
    <!-- `tabindex="-1"`: the dialog itself holds the focus until the field is
         up, and when nothing inside can; it is never in the tab order. -->
    <section
      ref="dialog"
      class="ai-tutor-equation-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-tutor-equation-dialog-title"
      tabindex="-1"
    >
      <h2
        id="ai-tutor-equation-dialog-title"
        class="ai-tutor-equation-dialog__title"
      >
        {{ title }}
      </h2>

      <span
        id="ai-tutor-equation-dialog-label"
        class="ai-tutor-equation-dialog__label"
      >
        {{ $t('aiTutorBoard.equation.inputLabel') }}
      </span>
      <!-- The math field is put here once it is loaded. -->
      <div
        ref="field"
        class="ai-tutor-equation-dialog__field"
        :class="{ 'ai-tutor-equation-dialog__field--refused': !!message }"
        data-testid="ai-tutor-equation-field"
        :data-field="fieldState"
        :data-state="state"
        :aria-busy="fieldState === 'loading' ? 'true' : 'false'"
      ></div>

      <!-- Keys act on the field and leave the keyboard in it: pressing one
           with the pointer never takes the focus (or the selection) away. -->
      <div
        ref="palette"
        class="ai-tutor-equation-dialog__palette"
        role="toolbar"
        dir="ltr"
        data-testid="ai-tutor-equation-palette"
        :aria-label="$t('aiTutorBoard.equation.palette')"
        @mousedown.prevent
        @keydown="onPaletteKeydown"
      >
        <div
          v-for="group in palette"
          :key="group.id"
          class="ai-tutor-equation-dialog__keys"
          role="group"
          :aria-label="$t(`aiTutorBoard.equation.groups.${group.id}`)"
        >
          <button
            v-for="key in group.keys"
            :key="key.id"
            type="button"
            class="ai-tutor-equation-dialog__key"
            :class="{ 'ai-tutor-equation-dialog__key--word': key.word }"
            :data-testid="`ai-tutor-equation-key-${key.id}`"
            :tabindex="key.index === rover ? 0 : -1"
            :disabled="fieldState !== 'ready'"
            :aria-label="$t(`aiTutorBoard.equation.keys.${key.id}`)"
            :title="$t(`aiTutorBoard.equation.keys.${key.id}`)"
            @click="onKey(key)"
          >
            <span
              v-if="key.over"
              class="ai-tutor-equation-dialog__fraction"
              aria-hidden="true"
            >
              <span>{{ key.glyph }}</span>
              <span>{{ key.over }}</span>
            </span>
            <span v-else aria-hidden="true">
              <sup v-if="key.pre">{{ key.pre }}</sup
              >{{ key.glyph }}<sup v-if="key.sup">{{ key.sup }}</sup
              ><sub v-if="key.sub">{{ key.sub }}</sub>
            </span>
          </button>
        </div>
      </div>

      <div
        class="ai-tutor-equation-dialog__status"
        role="status"
        aria-live="polite"
      >
        <p
          v-if="message"
          id="ai-tutor-equation-dialog-status"
          class="ai-tutor-equation-dialog__message"
          data-testid="ai-tutor-equation-message"
        >
          {{ message }}
        </p>
        <button
          v-if="retryable"
          type="button"
          class="ai-tutor-equation-dialog__retry"
          data-testid="ai-tutor-equation-retry"
          @click="onRetry"
        >
          {{ $t('aiTutorBoard.equation.retry') }}
        </button>
      </div>

      <div class="ai-tutor-equation-dialog__actions">
        <button
          type="button"
          class="ai-tutor-equation-dialog__primary"
          data-testid="ai-tutor-equation-submit"
          :disabled="!canSubmit"
          @click="onSubmit"
        >
          {{ primaryLabel }}
        </button>
        <button
          type="button"
          class="ai-tutor-equation-dialog__secondary"
          data-testid="ai-tutor-equation-cancel"
          @click="$emit('cancel')"
        >
          {{ $t('aiTutorBoard.equation.cancel') }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import {
  renderEquation,
  validateEquationSource,
} from '@/services/aiTutor/boardEquation'
import {
  BOARD_EQUATION_PALETTE,
  BOARD_EQUATION_SHORTCUTS,
  hasEmptySlot,
  loadEquationField,
} from '@/services/aiTutor/boardEquationField'

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Typing is not checked letter by letter.
const CHECK_DELAY_MS = 120

// What the dialog says for a draft the board's renderer refuses. Its typed
// failure codes only: nothing of the renderer, of the field or of the draft
// reaches the screen.
const REFUSALS = {
  empty: 'empty',
  rtl_text_unsupported: 'rtl',
  engine_unavailable: 'unavailable',
}

function refusalOf(err) {
  return REFUSALS[err && err.code] || 'invalid'
}

// The palette with one running index over all its keys: one of them is in
// the tab order, the arrow keys move among them.
let count = 0
const PALETTE = BOARD_EQUATION_PALETTE.map(group => ({
  id: group.id,
  keys: group.keys.map(key => {
    const indexed = Object.assign({ index: count }, key)
    count += 1
    return indexed
  }),
}))
const KEY_COUNT = count

export default {
  name: 'BoardEquationDialog',
  props: {
    mode: {
      type: String,
      default: 'insert',
      validator: mode => mode === 'insert' || mode === 'edit',
    },
    // EDIT: the source of the equation being edited. Read once, on open.
    source: { type: String, default: '' },
    // The Workspace is applying a submitted draft.
    busy: { type: Boolean, default: false },
    // Why the Workspace could not apply it: 'gone' (the equation is no longer
    // on the board), 'readOnly' (the board cannot be edited), or the board
    // editor's refusal of the source ('rtl', 'invalid', 'unavailable'). Shown
    // under the field.
    problem: { type: String, default: null },
  },
  data() {
    return {
      // 'loading' | 'ready' | 'unavailable' -- the visual field itself.
      fieldState: 'loading',
      // The draft, as LaTeX: the stored source until the Student edits.
      draft: this.source,
      // What the board's renderer says of the draft:
      // 'empty' | 'incomplete' | 'pending' | 'ok' | 'rtl' | 'invalid' |
      // 'unavailable'
      state: 'empty',
      // The draft the renderer last drew.
      drawn: null,
      // The palette key that is in the tab order.
      rover: 0,
      // {top, height} of the visual viewport, where the browser has one.
      viewport: null,
      pressedOutside: false,
    }
  },
  computed: {
    palette() {
      return PALETTE
    },
    title() {
      return this.$t(
        this.mode === 'edit'
          ? 'aiTutorBoard.equation.editTitle'
          : 'aiTutorBoard.equation.insertTitle'
      )
    },
    primaryLabel() {
      return this.$t(
        this.mode === 'edit'
          ? 'aiTutorBoard.equation.save'
          : 'aiTutorBoard.equation.insert'
      )
    },
    refused() {
      return (
        this.state === 'rtl' ||
        this.state === 'invalid' ||
        this.state === 'unavailable'
      )
    },
    message() {
      let key = this.problem
      if (!key && this.fieldState === 'unavailable') key = 'unavailable'
      if (!key && this.refused) key = this.state
      return key ? this.$t(`aiTutorBoard.equation.errors.${key}`) : ''
    },
    // The field, or the math engine, could not be loaded: try again.
    retryable() {
      return (
        !this.problem &&
        (this.fieldState === 'unavailable' || this.state === 'unavailable')
      )
    },
    // Only a draft the board's renderer has drawn, as it is now.
    canSubmit() {
      return (
        this.fieldState === 'ready' &&
        !this.busy &&
        this.problem !== 'gone' &&
        this.problem !== 'readOnly' &&
        this.state === 'ok' &&
        this.drawn === this.draft
      )
    },
    viewportStyle() {
      const viewport = this.viewport
      // `top` and `height` win over the stylesheet's `bottom: 0`.
      return viewport
        ? { top: `${viewport.top}px`, height: `${viewport.height}px` }
        : null
    },
  },
  created() {
    this.destroyed = false
    this.seq = 0
    this.timer = null
    // The field's handle, and how the field itself writes the source it was
    // opened on. Kept off `data`: Vue must not walk the field.
    this.field = null
    this.opened = ''
  },
  mounted() {
    // Focus restoration target: whatever raised the dialog. Captured BEFORE
    // focus moves into it.
    this.previouslyFocused =
      document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : null
    document.addEventListener('keydown', this.onKeydown)
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewport)
      window.visualViewport.addEventListener('scroll', this.onViewport)
      this.onViewport()
    }
    if (this.$refs.dialog) this.$refs.dialog.focus()
    this.openField()
  },
  beforeDestroy() {
    this.destroyed = true
    clearTimeout(this.timer)
    document.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = this.previousBodyOverflow || ''
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewport)
      window.visualViewport.removeEventListener('scroll', this.onViewport)
    }
    if (this.field) this.field.destroy()
    this.field = null
    const target = this.previouslyFocused
    if (target && typeof target.focus === 'function' && target.isConnected) {
      target.focus()
    }
  },
  methods: {
    // Loads the visual field (its own chunk, once per page) and opens it on
    // the source.
    async openField() {
      this.fieldState = 'loading'
      let field = null
      try {
        const engine = await loadEquationField()
        if (this.destroyed || this.field) return
        field = engine.createField(this.$refs.field, {
          value: this.source,
          label: this.$t('aiTutorBoard.equation.inputLabel'),
          shortcuts: BOARD_EQUATION_SHORTCUTS,
          onEdit: this.onEdit,
        })
      } catch (err) {
        if (!this.destroyed) this.fieldState = 'unavailable'
        return
      }
      this.field = field
      this.opened = field.getValue()
      this.fieldState = 'ready'
      field.focus()
      this.check()
    },
    // The Student changed the field (typing, a palette key).
    onEdit() {
      if (this.destroyed || !this.field) return
      const written = this.field.getValue()
      // Back to exactly what it was opened on: still the stored source,
      // byte for byte -- the field may write the same mathematics
      // differently.
      this.draft = written === this.opened ? this.source : written
      this.$emit('edited')
      this.check()
    },
    onKey(key) {
      if (!this.field || this.busy) return
      this.rover = key.index
      this.field.insert(key.latex, !!key.slot)
    },
    // The draft as it is now: refused at once by the validator (which never
    // loads the engine), or drawn a moment later.
    check() {
      clearTimeout(this.timer)
      this.seq += 1
      const seq = this.seq
      const draft = this.draft
      // A structure with a slot still empty is unfinished, not wrong.
      if (draft.trim() !== '' && hasEmptySlot(draft)) {
        this.state = 'incomplete'
        return
      }
      try {
        validateEquationSource(draft)
      } catch (err) {
        this.state = refusalOf(err)
        return
      }
      this.state = 'pending'
      this.timer = setTimeout(() => this.draw(draft, seq), CHECK_DELAY_MS)
    },
    async draw(draft, seq) {
      let refusal = null
      try {
        await renderEquation(draft)
      } catch (err) {
        refusal = refusalOf(err)
      }
      // The draft moved on while this one was being drawn.
      if (this.destroyed || seq !== this.seq) return
      if (refusal) {
        this.state = refusal
        return
      }
      this.drawn = draft
      this.state = 'ok'
    },
    onRetry() {
      if (this.fieldState === 'unavailable') this.openField()
      else this.check()
    },
    onSubmit() {
      if (this.canSubmit) this.$emit('submit', this.draft)
    },
    // A click on the backdrop closes; a selection dragged out of the field
    // and released over the backdrop is not such a click.
    onBackdropClick() {
      if (this.pressedOutside) this.$emit('cancel')
      this.pressedOutside = false
    },
    onViewport() {
      const viewport = window.visualViewport
      if (!viewport) return
      this.viewport = { top: viewport.offsetTop, height: viewport.height }
    },
    // The palette is one stop in the tab order; the arrow keys move among
    // its keys (they are laid out left to right).
    onPaletteKeydown(e) {
      const steps = { ArrowRight: 1, ArrowLeft: -1 }
      let next = null
      if (steps[e.key]) {
        next = (this.rover + steps[e.key] + KEY_COUNT) % KEY_COUNT
      } else if (e.key === 'Home') next = 0
      else if (e.key === 'End') next = KEY_COUNT - 1
      if (next === null) return
      e.preventDefault()
      this.rover = next
      const keys = this.$refs.palette.querySelectorAll('button')
      if (keys[next]) keys[next].focus()
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('cancel')
        return
      }
      if (e.key === 'Tab') this.trapFocus(e)
    },
    // Keep Tab / Shift+Tab inside the dialog while it is open.
    trapFocus(e) {
      const root = this.$refs.dialog
      if (!root) return
      const focusable = Array.from(root.querySelectorAll(FOCUSABLE))
      if (focusable.length === 0) {
        e.preventDefault()
        root.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      const inside = root.contains(active)
      if (e.shiftKey) {
        if (!inside || active === first || active === root) {
          e.preventDefault()
          last.focus()
        }
      } else if (!inside || active === last) {
        e.preventDefault()
        first.focus()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.ai-tutor-equation-dialog__backdrop {
  position: fixed;
  inset: 0;
  // Above the student bottom nav (40), the chat sheet (61) and the sidebar,
  // like the shell's own dialogs.
  z-index: 160;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 20px;
  background: rgba(7, 20, 71, 0.38);
  font-family: $borna-font-family;
}

.ai-tutor-equation-dialog {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: min(100%, 520px);
  max-height: 100%;
  padding: 20px 22px 22px;
  overflow-x: hidden;
  overflow-y: auto;
  border-radius: 24px;
  outline: none;
  background: $borna-surface;
  box-shadow: 0 22px 64px rgba(7, 20, 71, 0.2);
  color: $borna-text;
}

.ai-tutor-equation-dialog__title {
  margin: 0 0 12px;
  color: $borna-ink;
  font-size: 17px;
  font-weight: $borna-fw-medium;
  line-height: 1.6;
}

.ai-tutor-equation-dialog__label {
  margin: 0 0 6px;
  color: $borna-muted;
  font-size: 12px;
  font-weight: $borna-fw-bold;
}

// The math field's frame. Mathematics is left-to-right whatever the page
// direction.
.ai-tutor-equation-dialog__field {
  display: flex;
  flex: none;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 72px;
  max-height: 30vh;
  overflow: auto;
  border: 1px solid $borna-border;
  border-radius: 13px;
  background: $borna-surface;
  direction: ltr;

  &:focus-within {
    border-color: $borna-indigo-300;
    outline: 2px solid $borna-indigo-100;
    outline-offset: 0;
  }

  &--refused {
    border-color: $borna-error-border;
  }
}

.ai-tutor-equation-dialog__palette {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin: 12px 0 0;
}

.ai-tutor-equation-dialog__keys {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.ai-tutor-equation-dialog__key {
  display: grid;
  flex: none;
  place-items: center;
  box-sizing: border-box;
  min-width: 36px;
  height: 36px;
  padding: 0 6px;
  border: 1px solid $borna-border;
  border-radius: 10px;
  background: $borna-canvas;
  color: $borna-text;
  // Keys show mathematics: a Latin serif, whatever the page font.
  font-family: 'Times New Roman', Times, serif;
  font-size: 17px;
  line-height: 1;
  cursor: pointer;

  &--word {
    min-width: 44px;
    font-size: 15px;
  }

  &:hover,
  &:focus-visible {
    border-color: $borna-indigo-300;
    background: $borna-indigo-50;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  sup,
  sub {
    font-size: 0.68em;
  }
}

// The fraction key: one small term over another.
.ai-tutor-equation-dialog__fraction {
  display: grid;
  justify-items: center;
  font-size: 12px;
  font-style: italic;
  line-height: 1.05;

  span:first-child {
    padding: 0 3px 1px;
    border-bottom: 1px solid currentColor;
  }

  span:last-child {
    padding-top: 1px;
  }
}

.ai-tutor-equation-dialog__status {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 16px;
  margin: 8px 0;
}

.ai-tutor-equation-dialog__message {
  flex: 1;
  margin: 0;
  color: $borna-error-text;
  font-size: 13px;
  line-height: 1.8;
}

.ai-tutor-equation-dialog__retry {
  flex: none;
  height: 32px;
  padding: 0 14px;
  border: 1px solid $borna-indigo-100;
  border-radius: 10px;
  background: $borna-surface;
  color: $borna-indigo-900;
  font-family: inherit;
  font-size: 12px;
  font-weight: $borna-fw-extrabold;
  cursor: pointer;
}

.ai-tutor-equation-dialog__actions {
  display: grid;
  flex: none;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.ai-tutor-equation-dialog__primary,
.ai-tutor-equation-dialog__secondary {
  min-height: 44px;
  padding: 0 12px;
  border-radius: 13px;
  font-family: inherit;
  font-size: 13px;
  font-weight: $borna-fw-extrabold;
  cursor: pointer;
}

.ai-tutor-equation-dialog__primary {
  border: 1px solid transparent;
  background: $borna-indigo-600;
  color: #fff;

  &:hover,
  &:focus-visible {
    background: $borna-indigo-900;
  }

  &:disabled {
    background: $borna-indigo-100;
    color: $borna-indigo-600;
    cursor: not-allowed;
  }
}

.ai-tutor-equation-dialog__secondary {
  border: 1px solid $borna-border;
  background: $borna-surface;
  color: $borna-text;

  &:hover,
  &:focus-visible {
    background: $borna-canvas;
  }
}

.ai-tutor-equation-dialog button:focus-visible {
  outline: 2px solid $borna-indigo-600;
  outline-offset: 3px;
}

// The phone tier: a sheet at the bottom of the visible viewport. The palette
// is a strip two keys high that scrolls sideways inside the sheet, so the
// sheet stays short enough to sit above the on-screen keyboard.
@include borna-phone {
  .ai-tutor-equation-dialog__backdrop {
    align-items: flex-end;
    padding: 12px;
  }

  .ai-tutor-equation-dialog {
    width: 100%;
    padding: 16px;
    border-radius: 24px 24px 18px 18px;
  }

  .ai-tutor-equation-dialog__field {
    min-height: 64px;
  }

  .ai-tutor-equation-dialog__palette {
    flex-wrap: nowrap;
    gap: 0 12px;
    margin-top: 10px;
    padding-bottom: 2px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .ai-tutor-equation-dialog__keys {
    display: grid;
    flex: none;
    grid-auto-columns: max-content;
    grid-auto-flow: column;
    grid-template-rows: repeat(2, 40px);
  }

  .ai-tutor-equation-dialog__key {
    min-width: 40px;
    height: 40px;

    &--word {
      min-width: 48px;
    }
  }
}
</style>

<style lang="scss">
// Not scoped: the math field is created by its own library, outside Vue's
// templates. Every rule is prefixed with this component's field frame.
.ai-tutor-equation-dialog__field math-field {
  display: block;
  flex: 1;
  box-sizing: border-box;
  min-width: 0;
  padding: 10px 12px;
  border: 0;
  outline: none;
  background: transparent;
  color: $borna-ink;
  font-size: 22px;
  --caret-color: #{$borna-indigo-600};
  --selection-background-color: #{$borna-indigo-100};
  --selection-color: #{$borna-ink};
  --contains-highlight-background-color: transparent;
  --placeholder-color: #{$borna-indigo-600};
}

// The field's own on-screen-keyboard and menu buttons: Borna's palette is
// the only on-screen input.
.ai-tutor-equation-dialog__field math-field::part(virtual-keyboard-toggle),
.ai-tutor-equation-dialog__field math-field::part(menu-toggle) {
  display: none;
}
</style>
